"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, SearchX, SlidersHorizontal, X } from "lucide-react";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { cn } from "@/shared/lib/utils";
import type { SiteFooterContent, SiteNavbarContent } from "@/shared/types/site-shell-content";
import type { Gender, SearchResultsResponse } from "../../types";
import { searchRepository } from "../../data/repositories/search.repository";
import { HealerResultCard, ResultCardSkeleton } from "../components/search-result-cards";
const PAGE_LIMIT = 12;
export interface HealerListingFilters {
  city?: string;
  state?: string;
  language?: string;
  category?: string;
  gender?: Gender;
  minExperience?: number;
  maxExperience?: number;
}
interface HealerListingPageProps {
  navigation: SiteNavbarContent;
  footer: SiteFooterContent;
  locale: AppLocale;
  initialFilters: HealerListingFilters;
  initialPage: number;
  initialData: SearchResultsResponse | null;
}
const GENDER_OPTIONS: {
  value: Gender;
  en: string;
  hi: string;
}[] = [{
  value: "FEMALE",
  en: "Woman",
  hi: "महिला"
}, {
  value: "MALE",
  en: "Man",
  hi: "पुरुष"
}, {
  value: "OTHER",
  en: "Other",
  hi: "अन्य"
}];
function buildQueryString(filters: HealerListingFilters, page: number): string {
  const params = new URLSearchParams();
  if (filters.city) params.set("city", filters.city);
  if (filters.state) params.set("state", filters.state);
  if (filters.language) params.set("language", filters.language);
  if (filters.category) params.set("category", filters.category);
  if (filters.gender) params.set("gender", filters.gender);
  if (filters.minExperience != null) params.set("minExperience", String(filters.minExperience));
  if (filters.maxExperience != null) params.set("maxExperience", String(filters.maxExperience));
  if (page > 1) params.set("page", String(page));
  return params.toString();
}
export function HealerListingPage({
  navigation,
  footer,
  locale,
  initialFilters,
  initialPage,
  initialData
}: HealerListingPageProps) {
  const isHindi = locale === "hi";
  const [filters, setFilters] = useState<HealerListingFilters>(initialFilters);
  const [page, setPage] = useState(initialPage);
  const [data, setData] = useState<SearchResultsResponse | null>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const isFirstRun = useRef(true);
  const runSearch = useCallback(async (nextFilters: HealerListingFilters, nextPage: number) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(false);
    try {
      const response = await searchRepository.getResults({
        type: "healers",
        page: nextPage,
        limit: PAGE_LIMIT,
        includeFacets: false,
        ...nextFilters
      }, controller.signal);
      setData(response);
    } catch {
      if (controller.signal.aborted) return;
      setError(true);
      setData(null);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (isFirstRun.current) {
      // Skip refetching on mount — the server already provided initialData
      // for the initial filters/page combination.
      isFirstRun.current = false;
      return;
    }
    void runSearch(filters, page);
  }, [filters, page, runSearch]);
  useEffect(() => {
    const qs = buildQueryString(filters, page);
    const url = localizeHref(locale, `/healers${qs ? `?${qs}` : ""}`);
    window.history.replaceState(null, "", url);
  }, [filters, page, locale]);
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);
  function updateFilter<K extends keyof HealerListingFilters>(key: K, value: HealerListingFilters[K]) {
    setPage(1);
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }
  function clearFilters() {
    setPage(1);
    setFilters({});
  }
  const healers = data?.results.healers?.items ?? [];
  const total = data?.results.healers?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const hasActiveFilters = Object.values(filters).some(value_0 => value_0 !== undefined && value_0 !== "");
  return <div className="relative min-h-screen overflow-x-hidden bg-[#FAF8F5] font-sans text-slate-900 selection:bg-[#155A7D]/20">
      <SiteNavbar content={navigation} />

      <section className="relative overflow-hidden border-b border-[#EAE5DA]/60 bg-gradient-to-b from-[#0E2A3A] via-[#13415A] to-[#155A7D] px-6 pt-32 pb-14 text-white sm:pt-36">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#7DE2F8]/10 blur-3xl" />
        <div className="relative mx-auto w-full max-w-6xl text-center">
          <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">
            {isHindi ? <>
                अपना <span className="font-semibold">हीलर</span> खोजें
              </> : <>
                Find your <span className="font-semibold">healer</span>
              </>}
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm font-light text-white/70">
            {isHindi ? "विशेषज्ञता, भाषा या स्थान के अनुसार सत्यापित हीलर खोजें और फ़िल्टर करें।" : "Browse and filter verified healers by specialty, language, or location."}
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="mb-8 flex flex-col gap-4 border-b border-[#EAE5DA]/60 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <button type="button" onClick={() => setShowFilters(prev_0 => !prev_0)} className="inline-flex items-center gap-2 self-start rounded-full border border-[#EAE5DA] bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-[#155A7D]/40 sm:hidden">
            <SlidersHorizontal className="h-4 w-4" />
            {isHindi ? "फ़िल्टर" : "Filters"}
          </button>

          {!loading && !error && <p className="text-sm font-light text-slate-500">
              {total > 0 ? <>
                  <span className="font-semibold text-slate-900">{total}</span>{" "}
                  {isHindi ? "हीलर मिले" : "healers found"}
                </> : <>{isHindi ? "कोई हीलर नहीं मिला" : "No healers found"}</>}
            </p>}
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
          {/* Filter toolbar */}
          <aside className={cn("space-y-5 rounded-2xl border border-[#EAE5DA]/70 bg-white p-5", showFilters ? "block" : "hidden lg:block")}>
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-semibold text-[#0E2A3A]">
                {isHindi ? "फ़िल्टर" : "Filters"}
              </h2>
              {hasActiveFilters && <button type="button" onClick={clearFilters} className="flex items-center gap-1 text-xs font-medium text-[#C06A42] hover:underline">
                  <X className="h-3 w-3" />
                  {isHindi ? "साफ़ करें" : "Clear"}
                </button>}
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">
                {isHindi ? "विशेषज्ञता" : "Category"}
              </label>
              <input type="text" value={filters.category ?? ""} onChange={event => updateFilter("category", event.target.value || undefined)} placeholder={isHindi ? "जैसे, चिंता" : "e.g. anxiety"} className="mt-1.5 w-full rounded-lg border border-[#EAE5DA] px-3 py-2 text-sm focus:border-[#155A7D] focus:outline-none" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">
                {isHindi ? "भाषा" : "Language"}
              </label>
              <input type="text" value={filters.language ?? ""} onChange={event_0 => updateFilter("language", event_0.target.value || undefined)} placeholder={isHindi ? "जैसे, हिंदी" : "e.g. Hindi"} className="mt-1.5 w-full rounded-lg border border-[#EAE5DA] px-3 py-2 text-sm focus:border-[#155A7D] focus:outline-none" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">
                {isHindi ? "शहर" : "City"}
              </label>
              <input type="text" value={filters.city ?? ""} onChange={event_1 => updateFilter("city", event_1.target.value || undefined)} placeholder={isHindi ? "जैसे, मुंबई" : "e.g. Mumbai"} className="mt-1.5 w-full rounded-lg border border-[#EAE5DA] px-3 py-2 text-sm focus:border-[#155A7D] focus:outline-none" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">
                {isHindi ? "राज्य" : "State"}
              </label>
              <input type="text" value={filters.state ?? ""} onChange={event_2 => updateFilter("state", event_2.target.value || undefined)} placeholder={isHindi ? "जैसे, महाराष्ट्र" : "e.g. Maharashtra"} className="mt-1.5 w-full rounded-lg border border-[#EAE5DA] px-3 py-2 text-sm focus:border-[#155A7D] focus:outline-none" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">
                {isHindi ? "लिंग" : "Gender"}
              </label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {GENDER_OPTIONS.map(option => <button key={option.value} type="button" onClick={() => updateFilter("gender", filters.gender === option.value ? undefined : option.value)} className={cn("rounded-full border px-3 py-1.5 text-xs font-medium transition", filters.gender === option.value ? "border-[#155A7D] bg-[#155A7D]/10 text-[#155A7D]" : "border-[#EAE5DA] text-slate-500 hover:border-[#155A7D]/40")}>
                    {isHindi ? option.hi : option.en}
                  </button>)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500">
                  {isHindi ? "न्यूनतम अनुभव" : "Min. exp."}
                </label>
                <input type="number" min={0} value={filters.minExperience ?? ""} onChange={event_3 => updateFilter("minExperience", event_3.target.value ? Number(event_3.target.value) : undefined)} className="mt-1.5 w-full rounded-lg border border-[#EAE5DA] px-3 py-2 text-sm focus:border-[#155A7D] focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">
                  {isHindi ? "अधिकतम अनुभव" : "Max. exp."}
                </label>
                <input type="number" min={0} value={filters.maxExperience ?? ""} onChange={event_4 => updateFilter("maxExperience", event_4.target.value ? Number(event_4.target.value) : undefined)} className="mt-1.5 w-full rounded-lg border border-[#EAE5DA] px-3 py-2 text-sm focus:border-[#155A7D] focus:outline-none" />
              </div>
            </div>

            {/* Session mode / price / duration / rating filters are part of
                the product spec but the /search backend does not accept
                params for them yet — intentionally not wired to avoid
                filters that silently do nothing. */}
          </aside>

          {/* Results grid */}
          <div>
            {loading && <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {Array.from({
              length: 6
            }).map((_, index) => <ResultCardSkeleton key={index} />)}
              </div>}

            {!loading && error && <div className="rounded-2xl border border-dashed border-rose-200 bg-rose-50/50 py-16 text-center">
                <SearchX className="mx-auto mb-3 h-9 w-9 text-rose-400" />
                <p className="font-light text-slate-600">
                  {isHindi ? "हीलर लोड नहीं हो सके। कृपया पुनः प्रयास करें।" : "Could not load healers. Please try again."}
                </p>
              </div>}

            {!loading && !error && healers.length === 0 && <div className="rounded-2xl border border-dashed border-[#EAE5DA] bg-white/40 py-20 text-center">
                <SearchX className="mx-auto mb-4 h-10 w-10 text-slate-400" />
                <p className="font-heading text-lg font-light text-slate-700">
                  {isHindi ? "आपके फ़िल्टर से मेल खाने वाला कोई हीलर नहीं मिला।" : "No healers found matching your filters."}
                </p>
              </div>}

            {!loading && !error && healers.length > 0 && <>
                <motion.div initial="hidden" animate="show" variants={{
              hidden: {
                opacity: 0
              },
              show: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.05
                }
              }
            }} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {healers.map(healer => <HealerResultCard key={healer.id} healer={healer} locale={locale} />)}
                </motion.div>

                {totalPages > 1 && <div className="mt-10 flex items-center justify-center gap-3">
                    <button type="button" disabled={page <= 1} onClick={() => setPage(prev_1 => Math.max(1, prev_1 - 1))} className="rounded-full border border-[#EAE5DA] px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#155A7D]/40 disabled:cursor-not-allowed disabled:opacity-40">
                      {isHindi ? "पिछला" : "Previous"}
                    </button>
                    <span className="text-sm text-slate-500">
                      {page} / {totalPages}
                    </span>
                    <button type="button" disabled={page >= totalPages} onClick={() => setPage(prev_2 => Math.min(totalPages, prev_2 + 1))} className="rounded-full border border-[#EAE5DA] px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#155A7D]/40 disabled:cursor-not-allowed disabled:opacity-40">
                      {isHindi ? "अगला" : "Next"}
                    </button>
                  </div>}
              </>}

            {loading && <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {isHindi ? "लोड हो रहा है..." : "Loading..."}
              </div>}
          </div>
        </div>
      </section>

      <SiteFooter content={footer} />
    </div>;
}